import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatClp } from '../data/operationCatalogs';

import './OpsCreateFooter.scss';

export interface OpsCreateFooterMetrics {
  turnos: number;
  people: number;
  hours: number;
  cost: number;
}

interface OpsCreateFooterProps {
  mode: 'brief' | 'turns';
  /** create = nuevo/borrador; edit = editar RQ enviado */
  variant?: 'create' | 'edit';
  metrics: OpsCreateFooterMetrics;
  savingDraft?: boolean;
  submitting?: boolean;
  canContinue?: boolean;
  canSubmit?: boolean;
  onDraft: () => void;
  onContinue?: () => void;
  onSubmit: () => void;
}

function OpsCreateFooter({
  mode,
  variant = 'create',
  metrics,
  savingDraft,
  submitting,
  canContinue = true,
  canSubmit = false,
  onDraft,
  onContinue,
  onSubmit,
}: OpsCreateFooterProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request.footer';
  const isEdit = variant === 'edit';

  const hoursLabel =
    metrics.hours % 1 ? metrics.hours.toFixed(1) : String(metrics.hours);

  return (
    <div className="ops-create-footer">
      <div className="ops-create-footer__metrics">
        <Tag className="ops-create-footer__metric">
          <b>{metrics.turnos}</b> {t(`${i18nBase}.metrics.turns`)}
        </Tag>
        <Tag color="cyan" className="ops-create-footer__metric">
          <b>{metrics.people}</b> {t(`${i18nBase}.metrics.people`)}
        </Tag>
        <Tag className="ops-create-footer__metric">
          <b>{hoursLabel}h</b> {t(`${i18nBase}.metrics.hours`)}
        </Tag>
        <Tag color="blue" className="ops-create-footer__metric">
          <b>{formatClp(metrics.cost)}</b> {t(`${i18nBase}.metrics.cost`)}
        </Tag>
      </div>

      <div className="ops-create-footer__actions">
        <span className="ops-create-footer__status">
          <span className="ops-create-footer__status-dot" />
          {t(`${i18nBase}.status.${isEdit ? 'editing' : 'draft'}`)}
        </span>
        {!isEdit && (
          <Button loading={savingDraft} onClick={onDraft}>
            {t(`${i18nBase}.buttons.draft`)}
          </Button>
        )}
        {mode === 'brief' ? (
          <Button type="primary" disabled={!canContinue} onClick={onContinue}>
            {t(`${i18nBase}.buttons.continue`)}
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={!canSubmit}
            loading={submitting}
            onClick={onSubmit}
            title={
              canSubmit
                ? t(`${i18nBase}.tooltips.${isEdit ? 'save_ok' : 'submit_ok'}`)
                : t(`${i18nBase}.tooltips.submit_blocked`)
            }
          >
            {t(`${i18nBase}.buttons.${isEdit ? 'save' : 'submit'}`)}
          </Button>
        )}
      </div>
    </div>
  );
}

export default OpsCreateFooter;
